import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, OnInit, inject, Inject } from '@angular/core';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';

@Component({
  selector: 'app-press-media',
  standalone: true,
  imports: [CommonModule,RouterModule],
  templateUrl: './press-media.html',
  styleUrl: './press-media.css',
})
export class PressMedia implements OnInit {
  
  // Inject HttpClient
  private http = inject(HttpClient);

    constructor(
    @Inject(DOCUMENT) private document: Document,
    private title: Title,
    private meta: Meta,
  ) { }

  // Array to hold press media data
  pressMediaItems: any[] = [];

  // Awards grouped by AwardTitle
  awardGroups: { title: string; items: any[] }[] = [];
  isLoadingAwards: boolean = false;
  awardsError: boolean = false;

 // private readonly apiBase = 'https://crm-api.redmonkwellness.com';
 private readonly apiBase = 'https://crm-api.redmonkwellness.com';
  
  // Loading state
  isLoading: boolean = false;
  
  // Error message
  errorMessage: string = '';
  
  // Success message (optional, for debugging)
  successMessage: string = '';

  ngOnInit() {
    this.setSeo()
    this.loadPressMedia();
    this.loadAwards();
  }

  // Method to fetch press media data
  // In your loadPressMedia() method
  loadPressMedia() {
    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';
    
    const apiUrl = 'https://crm-api.redmonkwellness.com/Navigation/GetAllPressMedia';
    
    this.http.get<any>(apiUrl).subscribe({
      next: (response) => {
        console.log('API Response:', response);
        
        if (response.success && response.data) {
          this.pressMediaItems = response.data;
          // Log the first few image URLs to see what format they are
          this.pressMediaItems.slice(0, 3).forEach((item, index) => {
            console.log(`Item ${index} ImageURL:`, item.ImageURL);
            console.log(`Item ${index} Full ImageURL:`, 'https://crm-api.redmonkwellness.com/' + item.ImageURL);
          });
          this.successMessage = `Loaded ${this.pressMediaItems.length} press media items`;
        } else {
          this.errorMessage = response.message || 'Failed to load press media';
          this.pressMediaItems = [];
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading press media:', error);
        this.errorMessage = `Failed to load press media: ${error.message || 'Unknown error'}`;
        this.isLoading = false;
        this.pressMediaItems = [];
      }
    });
  }

  // Fetch active award photos (already ordered by DisplayOrder) and group them by title
  loadAwards() {
    this.isLoadingAwards = true;
    this.awardsError = false;

    this.http.get<any>(`${this.apiBase}/Navigation/GetAllAwards`).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.awardGroups = this.groupAwards(response.data);
        } else {
          this.awardsError = true;
        }
        this.isLoadingAwards = false;
      },
      error: (error) => {
        console.error('Error loading awards:', error);
        this.awardsError = true;
        this.isLoadingAwards = false;
      }
    });
  }

  // Keeps the order in which each award title first appears
  private groupAwards(items: any[]): { title: string; items: any[] }[] {
    const groups: { title: string; items: any[] }[] = [];
    for (const item of items) {
      const title = (item.AwardTitle || '').trim();
      let group = groups.find(g => g.title === title);
      if (!group) {
        group = { title, items: [] };
        groups.push(group);
      }
      group.items.push(item);
    }
    return groups;
  }

  // Saved paths look like /Content/PressMedia/xxx.jpg
  awardImg(path: string): string {
    if (!path) return '';
    if (/^(https?:|data:)/i.test(path)) return path;
    return this.apiBase + (path.startsWith('/') ? path : '/' + path);
  }


  // Helper method to format date
  formatDate(dateString: string | null): string {
    if (!dateString) return '';
    
    try {
      // Handle different date formats from backend
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return dateString;
      }
      
      // Format: Oct 25, 2025
      const options: Intl.DateTimeFormatOptions = { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      };
      return date.toLocaleDateString('en-US', options);
    } catch (error) {
      console.warn('Date formatting error:', error);
      return dateString;
    }
  }

  // Extract date from item (prioritizes Date, then CreationDate)
  getItemDate(item: any): string {
    return this.formatDate(item.Date || item.CreationDate);
  }

  // Determine if link should use routerLink or href
  isInternalLink(url: string | null): boolean {
    if (!url) return false;
    return url.startsWith('/') || !url.includes('://');
  }

  // Handle image errors
  handleImageError(event: Event, item: any) {
    const img = event.target as HTMLImageElement;
    img.src = '/assets/images/default-news.jpg';
    console.warn(`Image failed to load: ${item.ImageURL}`);
  }





private setSeo(): void {

  // =========================
  // SEO DATA
  // =========================

  const pageTitle =
    'Red Monk Wellness Press & Media | News, Awards & Coverage';

  const description =
    'Explore Red Monk Wellness press and media coverage, awards, news features and highlights from leading publications and media platforms.';

  const canonicalUrl =
    'https://www.redmonkwellness.com/press-media';

  const ogImage =
    'https://www.redmonkwellness.com/assets/images/services/houseof-hydration-thumb.png';

  // =========================
  // TITLE
  // =========================

  this.title.setTitle(pageTitle);

  // =========================
  // BASIC META
  // =========================

  this.meta.updateTag({
    name: 'description',
    content: description
  });

  this.meta.updateTag({
    name: 'robots',
    content:
      'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
  });

  this.meta.updateTag({
    name: 'google-site-verification',
    content: '7QzARYHwPFlrxb_DWbe2CdSeHLHRU-oTXeKHpi89MXg'
  });

  // =========================
  // OPEN GRAPH
  // =========================

  this.meta.updateTag({
    property: 'og:title',
    content: pageTitle
  });

  this.meta.updateTag({
    property: 'og:description',
    content: description
  });

  this.meta.updateTag({
    property: 'og:image',
    content: ogImage
  });

  this.meta.updateTag({
    property: 'og:image:alt',
    content: 'Red Monk Wellness Press and Media Coverage'
  });

  this.meta.updateTag({
    property: 'og:image:type',
    content: 'image/png'
  });

  this.meta.updateTag({
    property: 'og:url',
    content: canonicalUrl
  });

  this.meta.updateTag({
    property: 'og:type',
    content: 'website'
  });

  this.meta.updateTag({
    property: 'og:locale',
    content: 'en_IN'
  });

  this.meta.updateTag({
    property: 'og:site_name',
    content: 'Red Monk Wellness'
  });

  // =========================
  // TWITTER / X
  // =========================

  this.meta.updateTag({
    name: 'twitter:card',
    content: 'summary_large_image'
  });

  this.meta.updateTag({
    name: 'twitter:title',
    content: pageTitle
  });

  this.meta.updateTag({
    name: 'twitter:description',
    content: description
  });

  this.meta.updateTag({
    name: 'twitter:image',
    content: ogImage
  });

  this.meta.updateTag({
    name: 'twitter:image:alt',
    content: 'Red Monk Wellness Press and Media Coverage'
  });

  // =========================
  // CANONICAL URL
  // =========================

  let canonical = this.document.querySelector(
    'link[rel="canonical"]'
  ) as HTMLLinkElement | null;

  if (!canonical) {
    canonical = this.document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    this.document.head.appendChild(canonical);
  }

  canonical.setAttribute('href', canonicalUrl);

  // =========================
  // SITEMAP
  // =========================

  let sitemap = this.document.querySelector(
    'link[rel="sitemap"]'
  ) as HTMLLinkElement | null;

  if (!sitemap) {
    sitemap = this.document.createElement('link');
    sitemap.setAttribute('rel', 'sitemap');
    sitemap.setAttribute('type', 'application/xml');
    sitemap.setAttribute('title', 'Sitemap');
    this.document.head.appendChild(sitemap);
  }

  sitemap.setAttribute(
    'href',
    'https://www.redmonkwellness.com/sitemap.xml'
  );

  // =========================
  // ORGANIZATION SCHEMA
  // =========================

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': 'https://www.redmonkwellness.com/#organization',
    name: 'Red Monk Wellness',
    url: 'https://www.redmonkwellness.com/',
    logo:
      'https://www.redmonkwellness.com/assets/images/Redmonk-Wellness-Logo-w.png',
    description:
      'Red Monk Wellness offers skin, hair, aesthetic and wellness treatments through its clinics in Mumbai and Kolkata.',
    sameAs: [
      'https://www.linkedin.com/company/red-monk-wellness',
      'https://www.facebook.com/redmonkwellness'
    ]
  };

  // =========================
  // COLLECTION PAGE SCHEMA
  // =========================

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    name: pageTitle,
    headline: 'Red Monk Wellness Press & Media',
    description,
    isPartOf: {
      '@id': 'https://www.redmonkwellness.com/#website'
    },
    about: {
      '@id': 'https://www.redmonkwellness.com/#organization'
    },
    inLanguage: 'en-IN'
  };

  // =========================
  // BREADCRUMB SCHEMA
  // =========================

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${canonicalUrl}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://www.redmonkwellness.com/home'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Press & Media',
        item: canonicalUrl
      }
    ]
  };

  // =========================
  // ADD / UPDATE JSON-LD SCHEMAS
  // =========================

  const schemas = [
    {
      id: 'organization-schema',
      data: organizationSchema
    },
    {
      id: 'webpage-schema',
      data: webpageSchema
    },
    {
      id: 'breadcrumb-schema',
      data: breadcrumbSchema
    }
  ];

  schemas.forEach(({ id, data }) => {
    let script = this.document.getElementById(
      id
    ) as HTMLScriptElement | null;

    if (!script) {
      script = this.document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      this.document.head.appendChild(script);
    }

    script.textContent = JSON.stringify(data);
  });
}



}